import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import * as companyActions from "actions/companyActions";
import Layout from "components/Layout";
import Loading from "components/Loading";
import Modal from "components/Modal";
import Switch from "components/Switch";
import SubscriptionRequest from "components/Profile/SubscriptionModal";
import { isTrial } from "utils/utils";
import { showModal, hideModal } from "actions/modalActions";
import { getAppName } from 'utils/utils';
import { FiChevronDown } from "react-icons/fi";

const BASIC_FEATURE_KEYS = [
	"featureShiftPlanningByFunctionAndLocation",
	"featureTimeTrackingGeo",
	"featureOvertimeAndNightRules",
	"featureTwoLevelAbsenceApproval",
	"featureLocationLimitedClockIns",
	"featureEmployeeCommunications",
	"featureShiftChangeApproval",
	"featureShiftPreferenceApproval",
];

const PRO_FEATURE_KEYS = [
	"featureAiShiftPlanning",
	"featureDemandForecast",
	"featureSignaturitDigitalSignature",
	"featureTabletKioskTerminal",
	"featureDocumentAndPayrollManagement",
	"featureWeekendPrioritySupport",
];

const BASIC_HIGHLIGHT_KEYS = BASIC_FEATURE_KEYS.slice(0, 4);
const PRO_HIGHLIGHT_KEYS = PRO_FEATURE_KEYS.slice(0, 4);
const ALL_PRO_FEATURE_KEYS = [...BASIC_FEATURE_KEYS, ...PRO_FEATURE_KEYS];

class MySubscription extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
			errors: {},
			user: this.props.user,
			prevUser: this.props.user,
			isYearly: true,
			expandedFeatureLists: {
				basic: false,
				pro: false,
			},
		};

		this.closeModal = this.closeModal.bind(this);
		this.openSubscriptionModal = this.openSubscriptionModal.bind(this);
	}

	async componentDidMount() {
		this.props.getSubscriptionTypes();
		const companyId = this.props.user.company.id;
		await this.props.getCompany(companyId);
		await this.props.getCountries();
		await this.props.getEmployeesCount();
	}

	closeModal() {
		this.props.hideModal();
	}

	openSubscriptionModal(selectedPlan) {
		const { isYearly } = this.state;
		const { t } = this.props;

		this.props.showModal(
			{
				open: true,
				title: t("subscription.manageSubscription"),
				style: { width: "650px" },
				content: (
					<SubscriptionRequest
						selected_plan={selectedPlan}
						isYearly={isYearly}
						handleClose={this.closeModal}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	handleSwitch = (event) => {
		this.setState({
			[event.target.id]: event.target.checked,
		});
	};

	toggleFeatureList = (planKey) => {
		this.setState((previousState) => ({
			expandedFeatureLists: {
				...previousState.expandedFeatureLists,
				[planKey]: !previousState.expandedFeatureLists[planKey],
			},
		}));
	};

	getContent() {
		const { t, user, loading, subscription_types, employees_count } = this.props;
		const { isYearly, expandedFeatureLists } = this.state;

		if (loading || subscription_types.length === 0) {
			return <Loading />;
		}

		const subscription_type = user.subscription.subscription_type;
		const planFree = subscription_types.find((plan) => plan.name === "Free");
		const planPro = subscription_types.find((plan) => plan.name === "Pro");
		const contractedEmployees =
			user.subscription.max_users ||
			subscription_type.max_users ||
			(planFree && planFree.max_users) ||
			0;
		const usagePercentage = contractedEmployees > 0
			? Math.min((employees_count / contractedEmployees) * 100, 100)
			: 0;
		const renderEmployeeUsage = () => (
			<div className="employee-usage">
				<p className="employee-usage-text">
					{t("subscription.employeeUsagePrefix")} {" "}
					<strong>{t("subscription.activeEmployeesCount", { count: employees_count })}</strong>{" "}
					{t("subscription.employeeUsageConnector")} {" "}
					<strong>
						{t("subscription.contractedEmployeesCount", { count: contractedEmployees })}
					</strong>
				</p>
				{contractedEmployees > 0 && (
					<div
						className="employee-usage-progress"
						role="progressbar"
						aria-valuenow={employees_count}
						aria-valuemin="0"
						aria-valuemax={contractedEmployees}
					>
						<span style={{ width: `${usagePercentage}%` }} />
					</div>
				)}
			</div>
		);
		const renderFeatures = (featureKeys) =>
			featureKeys.map((featureKey) => (
				<li key={featureKey}>{t(`subscription.${featureKey}`)}</li>
			));
		const renderFeatureSummary = (planKey, highlightKeys, allFeatureKeys) => {
			const isExpanded = expandedFeatureLists[planKey];
			const additionalFeatureKeys = allFeatureKeys.filter(
				(featureKey) => !highlightKeys.includes(featureKey)
			);
			const expandedListId = `${planKey}-included-features`;

			return (
				<div className="feature-summary">
					<h3 className="feature-summary-title">{t("subscription.highlightsTitle")}</h3>
					<ul className="lista-features lista-features-highlights">
						{renderFeatures(highlightKeys)}
					</ul>

					<button
						type="button"
						className="features-toggle"
						onClick={() => this.toggleFeatureList(planKey)}
						aria-expanded={isExpanded}
						aria-controls={expandedListId}
					>
						<span>
							{isExpanded
								? t("subscription.hideIncludedFeatures")
								: t("subscription.showIncludedFeatures", { count: allFeatureKeys.length })}
						</span>
						<FiChevronDown
							aria-hidden="true"
							className={`features-toggle-chevron ${isExpanded ? "is-expanded" : ""}`}
						/>
					</button>

					{isExpanded && (
						<ul id={expandedListId} className="lista-features lista-features-expanded">
							{renderFeatures(additionalFeatureKeys)}
						</ul>
					)}
				</div>
			);
		};

		return (
			<div className="container container-precios">
				<div className="row-switch row-switch-double">
					<span className={`label-mensual label-switch ${!isYearly ? "active" : ""}`}>
						<span className="hidden-xs">{t("subscription.paymentPrefix")}</span>
						{t("subscription.quarterlyLabel")}
					</span>

					<Switch
						controlId="isYearly"
						isOn={isYearly}
						handleToggle={this.handleSwitch}
					/>

					<span className={`label-anual label-switch ${isYearly ? "active" : ""}`}>
						<span className="hidden-xs">{t("subscription.paymentPrefix")}</span>
						{t("subscription.annualLabel")}
						<span className="oferta hidden"> {t("subscription.discount")}</span>
					</span>
				</div>

				<div className="row row-precios justify-content-center">
					{isTrial() && (
						<div className="col-sm-6">
							<article className="precios-item">
								<h2 className="precios-titulo">Trial</h2>

								<div className="precios-precio">
									<span className="precios-valor">0€</span>
									<p className="precios-periodo">{t("subscription.limitedTime")}</p>
								</div>

								<p className="plan-description">{t("subscription.basicPlanDescription")}</p>
								{renderFeatureSummary("basic", BASIC_HIGHLIGHT_KEYS, BASIC_FEATURE_KEYS)}
								{renderEmployeeUsage()}
								{subscription_type.name !== planPro.name ? (
									<button type="button" disabled className="btn btn-white">
										{t("subscription.currentPlan")}
									</button>
								) : (
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => this.openSubscriptionModal(planFree)}
									>
										{t("subscription.selectPlan")}
									</button>
								)}
							</article>
						</div>
					)}
					<div className="col-sm-6">
						<article className="precios-item">
							<h2 className="precios-titulo">Pro</h2>

							<div className="precios-precio">
								<span className="precios-valor">4€</span>
								<p className="precios-periodo">{t("subscription.perEmployeeMonth")}</p>
							</div>

							<p className="plan-description">{t("subscription.proPlanCardDescription")}</p>
							{renderFeatureSummary("pro", PRO_HIGHLIGHT_KEYS, ALL_PRO_FEATURE_KEYS)}

							{renderEmployeeUsage()}

							<button
								type="button"
								className="btn btn-primary"
								onClick={() => this.openSubscriptionModal(planPro)}
							>
								{subscription_type.name === planPro.name
									? t("subscription.editCurrentPlan")
									: t("subscription.selectPlan")}
							</button>
						</article>
					</div>
				</div>
				<p className="precio-sin-iva">{t("subscription.priceExcludesVat")}</p>
			</div>
		);
	}

	render() {
		const { t } = this.props;
		const pathname = this.props.location.pathname;
		const appName = getAppName();

		const pageTitle = pathname.includes("contract-plans")
			? t("subscription.contractPlansTitle", { appName })
			: t("subscription.pageTitle");

		return (
			<>
				<Layout className="page-subscription">
					<div className="heading">
						<h1 className="title">{pageTitle}</h1>
					</div>
					{this.getContent()}
				</Layout>

				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.companyReducer,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	getSubscriptionTypes: () => dispatch(companyActions.getSubscriptionTypes()),
	getCompany: (company_id) => dispatch(companyActions.getCompany(company_id)),
	getCountries: () => dispatch(companyActions.getCountries()),
	getEmployeesCount: () => dispatch(companyActions.getEmployeesCount()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(MySubscription));
